import React, { useState } from "react";
import {
  Grid,
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import logo from "../../../assets/img/Fronic_Logo_white.png";
import { useHistory } from "react-router-dom";
// import ReCAPTCHA from "react-google-recaptcha"; // Commented out - using IP validation instead
// import axios from "axios";
import { toastError, toastSuccess } from "../../../utils";
import { ChristmasHat, NewYearMagic } from "../../../components/Festival"; // Festival Theme - Auto-enabled based on date
import { getFestivalTheme } from "../../../utils/festivalThemes";

const StyledButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: "linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)",
  fontSize: 16,
  height: 50,
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(26, 115, 232, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1557b0 0%, #00a589 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 25px rgba(26, 115, 232, 0.4)",
  },
  "&:disabled": {
    background: "#555",
    color: "#999",
  },
});

const CssTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  "& label": {
    fontFamily: "'DM Sans', sans-serif",
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& label.Mui-focused": {
    color: "#00bfa5",
  },
  "& .MuiOutlinedInput-root": {
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00bfa5",
      borderWidth: "2px",
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "white",
    padding: "16px 14px",
  },
});

export default function Login(props) {
  // const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"; // Commented out - using IP validation instead
  // const [isCapchaGood, setIsCapchaGood] = useState(isLocalhost);
  const isCapchaGood = true; // Captcha disabled - using IP validation instead
  const [showPassword, setShowPassword] = useState(false);
  const [festivalTheme] = useState(getFestivalTheme()); // Get theme once on mount
  const {
    user,
    errors,
    onChangeHandler,
    onLoginIn,
    loginLoader,
    setShowforgot,
  } = props;
  const history = useHistory();

  const keyPressed = (e) => {
    if (e.key === "Enter") {
      if (!loginLoader && isCapchaGood) {
        onLoginIn();
      }
    }
  };

  //---capcha code starts-------------------------- (Commented out - using IP validation instead)

  // async function onChangeCapcha(value) {
  //   await axios
  //     .post(
  //       `https://backend.fronicmedia.com/api/capchaVerification?responseCapcha=${value}`
  //     )
  //     .then((res) => {
  //       if (res?.data?.body?.success === false) {
  //         setIsCapchaGood(false);
  //         toastError("Captcha expired. Try again ?");
  //       } else if (res?.data?.body?.success === true) {
  //         setIsCapchaGood(true);
  //         toastSuccess("Good to see you Human.");
  //       } else {
  //         toastError("Captcha verification failed.");
  //       }
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //     });
  // }

  //---code ends-----------------------------------

  return (
    <Fade in timeout={800}>
      <div className="login-div" style={{ position: "relative" }}>
        {/* Festival Themes - Auto-enabled based on date */}
        {festivalTheme === "christmas" && <ChristmasHat />}
        {festivalTheme === "newyear" && <NewYearMagic />}
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Zoom in timeout={600}>
              <div className="logo-image-div">
                <img src={logo} alt="Fronic Media" />
              </div>
            </Zoom>
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} mb={0.5}>
              <Fade in timeout={1000}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    mb: 3,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <span style={{ fontSize: "1.4rem", fontWeight: 700, color: "white" }}>
                    Welcome Back
                  </span>
                  <span style={{ fontSize: "1rem", fontWeight: 300, color: "rgba(255, 255, 255, 0.25)" }}>
                    |
                  </span>
                  <span style={{ fontSize: "0.8rem", fontWeight: 400, color: "rgba(255, 255, 255, 0.5)" }}>
                    Login to continue
                  </span>
                </Box>
              </Fade>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box mb={2.5}>
              <CssTextField
                label="Email Address"
                variant="outlined"
                value={user.email}
                error={!!errors.email}
                onChange={(e) => onChangeHandler("email", e)}
                fullWidth
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box mb={1.5}>
              <CssTextField
                type={showPassword ? "text" : "password"}
                value={user.password}
                error={!!errors.password}
                onChange={(e) => onChangeHandler("password", e)}
                label="Password"
                variant="outlined"
                onKeyPress={keyPressed}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box mb={3}>
              <Typography
                className="forgot-password"
                onClick={() => setShowforgot(true)}
                sx={{
                  cursor: "pointer",
                  textAlign: "right",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  transition: "color 0.2s ease",
                }}
              >
                Forgot Password?
              </Typography>
            </Box>
          </Grid>

          {/* Captcha commented out - using IP validation instead */}
          {/* {!isLocalhost && (
            <Grid item xs={12}>
              <Box mb={2.5} display="flex" justifyContent="center">
                <ReCAPTCHA
                  sitekey="6LdUG_gjAAAAAEXj3qkc5X-jlW4Z4JOpqPiopmmm"
                  onChange={onChangeCapcha}
                  theme="dark"
                />
              </Box>
            </Grid>
          )} */}

          <Grid item xs={12}>
            <Box mb={2}>
              <StyledButton
                variant="contained"
                onClick={onLoginIn}
                disabled={loginLoader}
                fullWidth
              >
                {loginLoader ? (
                  <CircularProgress size={24} style={{ color: "white" }} />
                ) : (
                  "Login"
                )}
              </StyledButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box textAlign="center">
              <Typography
                className="dont-have-account"
                onClick={() => history.push("/signup")}
                sx={{
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  transition: "color 0.2s ease",
                }}
              >
                Don't have an account?{" "}
                <span
                  style={{
                    color: "#00bfa5",
                    fontWeight: 600,
                  }}
                >
                  Sign Up
                </span>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </div>
    </Fade>
  );
}
